import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import { Menu as MenuIcon, Add } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import { COLORS } from '@elevair/shared';
import { NotificationBell, NotificationPanel } from '../notifications';
import AvatarWithBadge from '../common/AvatarWithBadge';
import { SubscriptionTier, UserRole } from '../common/TierBadge';
import { TrialBanner } from '../common/TrialBanner';
import { useSubscription } from '../../hooks/useSubscription';

const APPBAR_HEIGHT = 64;

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch fresh subscription data to ensure we always display the correct tier
  const { data: subscriptionData } = useSubscription();
  const currentTier = (subscriptionData?.subscription?.tier || organization?.subscription?.tier || 'free') as SubscriptionTier;

  // Auto-close sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Full-width Top AppBar - Fixed position */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {/* Hamburger Menu - Far left */}
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            sx={{
              mr: 2,
              color: 'text.primary',
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo - Always visible */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Georgia, serif',
              color: COLORS.primary,
              mr: 2,
            }}
          >
            Elevair
          </Typography>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* New Booking Button */}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/bookings')}
            sx={{
              mr: 2,
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            New Booking
          </Button>

          {/* Notification Bell */}
          <NotificationBell onClick={() => setNotificationPanelOpen(true)} />

          {/* User Avatar */}
          <Box sx={{ p: 0.5 }}>
            <AvatarWithBadge
              name={`${user?.profile.firstName} ${user?.profile.lastName}`}
              avatarUrl={user?.profile.avatar}
              avatarColor={user?.profile.avatarColor || COLORS.primary}
              tier={currentTier}
              userRole={user?.role as UserRole}
              size={36}
              badgeSize={14}
              onAvatarClick={handleMenuOpen}
              onBadgeClick={() => navigate('/settings/billing')}
            />
          </Box>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {user?.profile.firstName} {user?.profile.lastName}
              </Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
              Settings
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Spacer for fixed AppBar */}
      <Toolbar />

      {/* Content area below AppBar */}
      <Box sx={{ display: 'flex', flex: 1 }}>
        {/* Sidebar */}
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: `calc(100vh - ${APPBAR_HEIGHT}px)`,
            backgroundColor: '#F9FAFB',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          {/* Trial Banner - inside main content to respect sidebar */}
          <TrialBanner />

          <Box
            sx={{
              py: 3,
              pl: '10px',
              pr: { xs: 2, sm: 3, md: 4, lg: 6 },
            }}
          >
            <Box
              sx={{
                maxWidth: 1200,
              }}
            >
              <Outlet />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Notification Panel */}
      <NotificationPanel
        open={notificationPanelOpen}
        onClose={() => setNotificationPanelOpen(false)}
      />
    </Box>
  );
}
